import React, { useEffect, useState } from 'react'
import { HtmlContent, Grid, Tabs, Tab, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { PresentationBlockProps } from '../..'
import compareBlockProps from '../../util/compareBlockProps'
import CodeIcon from '@material-ui/icons/Code'
import { CodeEditor } from '../../components'
import { sanitizeCustomCss } from './sanitizeCustomCss'

// HtmlContent's sanitizer (striptags) only strips disallowed tags — it does not strip attributes
// off allowed ones. So this list must exclude any tag that can execute script, navigate, submit,
// or embed external content (form, iframe, input/button with event-handler attributes), since
// this content is rendered to customers in a public presentation. Keep it to inert formatting tags.
const additionalTags = ['pre', 'blockquote', 'code']

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: { position: 'relative' },
    content: {},
    tabs: {
      minHeight: 0,
      marginBottom: theme.spacing(1)
    },
    tab: {
      minHeight: 0,
      minWidth: 0,
      padding: theme.spacing(0.5, 2),
      textTransform: 'none',
      letterSpacing: 0
    },
    codeEditor: {
      fontSize: '0.875rem'
    },
    codeIcon: {
      position: 'absolute',
      bottom: 8,
      right: 8,
      opacity: 0.5,
      pointerEvents: 'none'
    }
  }
})

function CustomBlock(props: PresentationBlockProps) {
  const classes = useStyles(props)
  const { isEditable, isPreview, isSettings, block, blockPrefix, setFieldValue, submitForm, WrapperComponent } = props
  const { fields } = block
  const [tab, setTab] = useState<'html' | 'css'>('html')
  const [html, setHtml] = useState(fields?.html ?? '')
  const [css, setCss] = useState(fields?.css ?? '')

  useEffect(() => {
    setHtml(fields?.html ?? '')
  }, [fields?.html])

  useEffect(() => {
    setCss(fields?.css ?? '')
  }, [fields?.css])

  const handleEditField = (field: string, content: string) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={3} classes={{ root: classes.root }}>
        {isSettings && !isPreview && !isEditable && <CodeIcon className={classes.codeIcon} />}
        <Grid item md={12}>
          {isEditable ? (
            <>
              <Tabs
                className={classes.tabs}
                value={tab}
                onChange={(_ev, newTab) => setTab(newTab)}
                indicatorColor={'primary'}
              >
                <Tab className={classes.tab} value={'html'} label={'HTML'} />
                <Tab className={classes.tab} value={'css'} label={'CSS'} />
              </Tabs>
              {tab === 'html' && (
                <CodeEditor
                  language={'html'}
                  placeholder={'Add HTML'}
                  value={html}
                  onChange={setHtml}
                  onBlur={() => handleEditField('fields.html', html)}
                  className={classes.codeEditor}
                />
              )}
              {tab === 'css' && (
                <CodeEditor
                  language={'css'}
                  placeholder={'Add CSS'}
                  value={css}
                  onChange={setCss}
                  onBlur={() => handleEditField('fields.css', css)}
                  className={classes.codeEditor}
                />
              )}
            </>
          ) : (
            <HtmlContent
              includeTags={additionalTags}
              content={fields?.html ?? ''}
              classes={{ root: classes.content }}
            />
          )}
          <HtmlContent includeTags={['style']} content={`<style>${sanitizeCustomCss(fields?.css ?? '')}</style>`} />
        </Grid>
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(CustomBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
